// frontend/src/pages/CashUp.js
import React, { useState, useEffect } from "react";
import api from "../api";
import { useCurrency } from "../hooks/useCurrency";

function fmt_date(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function CashUp() {
  const { fmt, currency } = useCurrency();
  const [form, setForm] = useState({
    opening_float: "",
    actual_cash: "",
    seller_id: "",
    session_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [sellers, setSellers] = useState([]);
  const [dayRevenue, setDayRevenue] = useState(0);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const loadSessions = () => {
    setSessionsLoading(true);
    api
      .get("/api/cash-up")
      .then((r) => setSessions(r.data))
      .catch(() => {})
      .finally(() => setSessionsLoading(false));
  };

  useEffect(() => {
    api
      .get("/api/auth/users")
      .then((r) =>
        setSellers(
          r.data.filter((u) => ["seller", "manager"].includes(u.role)),
        ),
      )
      .catch(() => {});
    const today = new Date().toISOString().slice(0, 10);
    api
      .get(`/api/reports/sales?from=${today}&to=${today}`)
      .then((r) =>
        setDayRevenue(r.data.reduce((a, d) => a + Number(d.revenue), 0)),
      )
      .catch(() => {});
    loadSessions();
  }, []);

  const openingFloat = parseFloat(form.opening_float) || 0;
  const actualCash = parseFloat(form.actual_cash) || 0;
  const expectedCash = openingFloat + dayRevenue;
  const variance = actualCash - expectedCash;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/cash-up", {
        ...form,
        opening_float: openingFloat,
        expected_cash: expectedCash,
        actual_cash: actualCash,
      });
      setMsg("[OK] Cash up session saved successfully!");
      setMsgType("success");
      setForm({
        opening_float: "",
        actual_cash: "",
        seller_id: "",
        session_date: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      loadSessions();
    } catch (err) {
      setMsg("[X] " + (err.response?.data?.error || "Failed to save cash up"));
      setMsgType("error");
    }
    setTimeout(() => setMsg(""), 5000);
  };

  return (
    <div>
      <div className="pb-5 mb-6 border-b border-gray-200">
        <div className="text-xl sm:text-2xl font-bold text-gray-900">
          Lift / Cash Up
        </div>
        <div className="text-sm text-gray-400 mt-0.5">
          End-of-day cash reconciliation
        </div>
      </div>

      {msg && (
        <div
          className={`rounded-lg px-4 py-2.5 text-sm font-medium mb-4 border ${msgType === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
        >
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-bold text-gray-900 mb-5">
            New Cash Up Session
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Session Date
                </label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  type="date"
                  value={form.session_date}
                  onChange={(e) =>
                    setForm({ ...form, session_date: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Seller (optional)
                </label>
                <select
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 bg-white"
                  value={form.seller_id}
                  onChange={(e) =>
                    setForm({ ...form, seller_id: e.target.value })
                  }
                >
                  <option value="">All sellers</option>
                  {sellers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.role})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Opening Float ({currency})
                </label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  type="number"
                  value={form.opening_float}
                  onChange={(e) =>
                    setForm({ ...form, opening_float: e.target.value })
                  }
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Actual Cash in Till ({currency})
                </label>
                <input
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  type="number"
                  value={form.actual_cash}
                  onChange={(e) =>
                    setForm({ ...form, actual_cash: e.target.value })
                  }
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Notes
              </label>
              <textarea
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-500 resize-none"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Any discrepancies or comments..."
                rows={3}
              />
            </div>
            <button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-3 text-sm font-bold transition-colors"
              type="submit"
            >
              Save Cash Up Session
            </button>
          </form>
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4">
              Today's Summary
            </h3>
            <div className="flex flex-col gap-3">
              {[
                {
                  label: "Today's Revenue",
                  value: fmt(dayRevenue),
                  color: "text-blue-600",
                },
                {
                  label: "Opening Float",
                  value: fmt(openingFloat),
                  color: "text-gray-600",
                },
                {
                  label: "Expected in Till",
                  value: fmt(expectedCash),
                  color: "text-purple-600",
                  bold: true,
                },
                {
                  label: "Actual Cash",
                  value: fmt(actualCash),
                  color: "text-gray-700",
                },
              ].map(({ label, value, color, bold }) => (
                <div key={label} className="flex justify-between text-sm">
                  <span className="text-gray-400">{label}</span>
                  <span
                    className={`${bold ? "font-bold" : "font-semibold"} ${color}`}
                  >
                    {value}
                  </span>
                </div>
              ))}
              <div className="border-t-2 border-gray-200 pt-3 flex justify-between text-base font-bold">
                <span>Variance</span>
                <span
                  className={
                    variance === 0
                      ? "text-green-600"
                      : variance > 0
                        ? "text-blue-600"
                        : "text-red-600"
                  }
                >
                  {variance >= 0 ? "+" : ""}
                  {fmt(variance)}
                </span>
              </div>
            </div>
            {form.actual_cash !== "" && (
              <div
                className={`rounded-lg px-3 py-2.5 text-sm font-medium mt-4 border ${variance === 0 ? "bg-green-50 text-green-700 border-green-200" : variance > 0 ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-red-50 text-red-700 border-red-200"}`}
              >
                {variance === 0 && "[OK] Cash balanced perfectly!"}
                {variance > 0 && ` Till is over by ${fmt(Math.abs(variance))}`}
                {variance < 0 && ` Till is short by ${fmt(Math.abs(variance))}`}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-sm text-gray-500">
            <div className="font-bold text-gray-900 mb-3">How to Cash Up</div>
            <ol className="flex flex-col gap-2">
              {[
                "Count all cash in the till",
                "Enter the opening float (the starting cash)",
                "Enter actual cash counted",
                "System calculates the variance automatically",
                "Save the session for records",
              ].map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-blue-600 font-bold shrink-0">
                    {i + 1}.
                  </span>
                  {s}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/*  Past Sessions Log  */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-base font-bold text-gray-900">
              {" "}
              Past Cash Up Sessions
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              All saved sessions for this shop
            </div>
          </div>
          <button
            onClick={loadSessions}
            className="text-xs text-blue-600 hover:underline px-2 py-1"
          >
            Refresh
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {sessionsLoading ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              Loading sessions
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <div className="text-3xl mb-2"></div>
              <div className="text-sm font-medium">No sessions saved yet</div>
              <div className="text-xs mt-1">
                Sessions will appear here after you save them above
              </div>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Date
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Seller
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Manager
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Opening Float
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Expected
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Actual Cash
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Variance
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((s, i) => {
                      const variance =
                        Number(s.actual_cash) - Number(s.expected_cash);
                      return (
                        <tr
                          key={s.id}
                          className={`border-b border-gray-50 ${i % 2 === 0 ? "" : "bg-gray-50/40"} hover:bg-blue-50/30 transition-colors`}
                        >
                          <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                            {fmt_date(s.session_date)}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {s.seller_name || (
                              <span className="text-gray-400 italic">
                                All sellers
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {s.manager_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {fmt(s.opening_float)}
                          </td>
                          <td className="px-4 py-3 text-right text-purple-600 font-semibold">
                            {fmt(s.expected_cash)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-800 font-semibold">
                            {fmt(s.actual_cash)}
                          </td>
                          <td
                            className={`px-4 py-3 text-right font-bold ${variance === 0 ? "text-green-600" : variance > 0 ? "text-blue-600" : "text-red-600"}`}
                          >
                            {variance >= 0 ? "+" : ""}
                            {fmt(variance)}
                          </td>
                          <td
                            className="px-4 py-3 text-gray-500 max-w-[180px] truncate text-xs"
                            title={s.notes || ""}
                          >
                            {s.notes || (
                              <span className="text-gray-300 italic">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden flex flex-col divide-y divide-gray-100">
                {sessions.map((s) => {
                  const variance =
                    Number(s.actual_cash) - Number(s.expected_cash);
                  return (
                    <div key={s.id} className="p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-bold text-gray-900">
                          {fmt_date(s.session_date)}
                        </span>
                        <span
                          className={`font-bold text-sm ${variance === 0 ? "text-green-600" : variance > 0 ? "text-blue-600" : "text-red-600"}`}
                        >
                          {variance >= 0 ? "+" : ""}
                          {fmt(variance)}
                        </span>
                      </div>
                      {s.seller_name && (
                        <div className="text-xs text-gray-500 mb-1">
                          Seller: <b>{s.seller_name}</b>
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2 text-xs text-center mt-2">
                        <div className="bg-gray-50 rounded-lg p-2">
                          <div className="text-gray-400">Expected</div>
                          <div className="font-semibold text-purple-600">
                            {fmt(s.expected_cash)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <div className="text-gray-400">Actual</div>
                          <div className="font-semibold text-gray-800">
                            {fmt(s.actual_cash)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <div className="text-gray-400">Variance</div>
                          <div
                            className={`font-bold ${variance === 0 ? "text-green-600" : variance > 0 ? "text-blue-600" : "text-red-600"}`}
                          >
                            {variance >= 0 ? "+" : ""}
                            {fmt(variance)}
                          </div>
                        </div>
                      </div>
                      {s.notes && (
                        <div className="text-xs text-gray-400 italic mt-2">
                          {s.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
